import { partytownSnippet } from "@qwik.dev/partytown/integration";
import { NATIVE_GA_WORKER_LIB_PATH } from "./performance-critical-paths";

declare global {
  interface Window {
    ishNativeGtag?: (...args: unknown[]) => void;
  }
}

/**
 * The worker needs its own native Arguments objects: forwarding dataLayer.push
 * serializes those objects into a shape Google's command parser ignores.
 * Keep the browser queue too, so startup failure can replay it on the main thread.
 * @internal
 */
export function nativeGaWorkerBootstrap(commands: unknown[][]): string {
  const snapshot = JSON.stringify(commands).replace(/</g, "\\u003c");
  return `var queued=${snapshot};
if(document.currentScript.type==='text/partytown'){
  window.dataLayer=[];
}else{
  queued=[];
  window.dataLayer=window.dataLayer||[];
}
window.ishNativeGtag=function(){window.dataLayer.push(arguments)};
queued.forEach(function(command){
  if(command[0]==='js')command[1]=new Date(command[1]);
  window.ishNativeGtag.apply(null,command);
});`;
}

export function appendNativeGaWorker(script: HTMLScriptElement, onFailure: () => void) {
  if (!navigator.serviceWorker) {
    document.head.appendChild(script);
    return;
  }
  const commands = (window.dataLayer ?? [])
    .filter((entry) => Object.prototype.toString.call(entry) === "[object Arguments]")
    .map((entry) => Array.from(entry as ArrayLike<unknown>));
  window.ishNativeGtag = window.gtag;
  const bootstrap = document.createElement("script");
  bootstrap.id = "ga4-worker-bootstrap";
  bootstrap.type = "text/partytown";
  bootstrap.textContent = nativeGaWorkerBootstrap(commands);
  script.type = "text/partytown";

  // Partytown marks HTTP/execution failures on the original script. Its main
  // thread fallback clones script elements, so also retain the browser's
  // capturing resource-error boundary for that exact first-party loader URL.
  let reported = false;
  const fail = () => {
    if (!reported) onFailure();
    reported = true;
  };
  const observer = new MutationObserver(() => {
    if (script.hasAttribute("data-pterror")) fail();
    if (script.hasAttribute("data-pterror") || script.type !== "text/partytown") {
      observer.disconnect();
    }
  });
  observer.observe(script, { attributes: true, attributeFilter: ["data-pterror", "type"] });
  window.addEventListener(
    "error",
    (event) => {
      if (event.target instanceof HTMLScriptElement && event.target.src === script.src) fail();
    },
    true
  );

  document.head.appendChild(bootstrap);
  document.head.appendChild(script);
  const runtime = document.createElement("script");
  runtime.id = "ga4-worker-runtime";
  runtime.textContent = partytownSnippet({
    lib: NATIVE_GA_WORKER_LIB_PATH,
    // gtag can append a second loader with gtg_health=1. Worker fetches of
    // that Google URL lack CORS headers; the existing gateway serves it under
    // our unchanged script CSP. This hook is serialized, so keep it closure-free.
    resolveUrl: (url, location) => {
      if (url.origin === "https://www.googletagmanager.com" && url.pathname === "/gtag/js") {
        return new URL(`/metrics/gtag/js${url.search}`, location.origin);
      }
      return url;
    },
    forward: [["ishNativeGtag", { preserveBehavior: true }]],
  });
  document.head.appendChild(runtime);
  window.gtag = function gtag() {
    // eslint-disable-next-line prefer-rest-params
    window.ishNativeGtag?.apply(null, Array.from(arguments));
  };
}
